import Big from "big.js";
import { action, computed, makeObservable } from "mobx";
import { View } from "tamagui";
import { TKey } from "../../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../../activities/types";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../../core/BaseModuleDataModel";
import type ExpenseModuleModel from "../ExpenseModuleModel";

export interface Split {
  userId: string;
  amount: Big; // the amount this user owes for the expense
}

export interface ExpenseItemModuleData extends BaseModuleModelData {
  title: string;
  amount: Big;
  paidByUserId: string;
  splits: Split[];
}

export default class ExpenseItemModule extends BaseModuleDataModel<ExpenseItemModuleData> {
  constructor(stores: Stores, api: Api, data: ExpenseItemModuleData) {
    super(stores, api, data);

    Object.assign(this.data, {
      title: data.title ?? "",
      amount: new Big(data.amount ?? 0),
      paidByUserId: data.paidByUserId ?? "",
      splits:
        data.splits.map((split) => ({
          ...split,
          amount: new Big(split.amount),
        })) ?? [],
    });

    makeObservable(this);
  }

  @computed
  get paidByUser() {
    return this.plan.users.find((user) => user.uid === this.data.paidByUserId);
  }

  get splitsUser() {
    return this.data.splits.map((split) => ({
      ...split,
      user: this.plan.users.find((user) => user.uid === split.userId),
    }));
  }

  @action update(data: {
    title: string;
    amount: Big;
    paidByUserId: string;
    splits: Split[];
  }) {
    if (data.amount.lte(0)) {
      throw new Error("Expense amount cannot be zero or negative.");
    }

    const totalSplitsAmount = data.splits
      .reduce((total, split) => total.plus(split.amount), new Big(0))
      .toNumber();

    if (!data.amount.eq(totalSplitsAmount)) {
      throw new Error("Splits do not sum up to the total amount.");
    }

    const expenseModule = this.module as ExpenseModuleModel;

    const oldAmount = this.data.amount;

    this.data.title = data.title;
    this.data.amount = data.amount;
    this.data.paidByUserId = data.paidByUserId;
    this.data.splits = data.splits.map((split) => ({
      ...split,
      amount: split.amount,
    }));

    this.module.postActivityEvent(
      ActivityEventType.DataChanged,
      TKey.ActivityExpensesExpenseUpdated,
      {
        title: data.title,
        oldAmount: expenseModule.currencyFormatter.format(oldAmount.toNumber()),
        amount: expenseModule.currencyFormatter.format(data.amount.toNumber()),
      },
    );

    return this.api.modulesDataApi.set(this);
  }

  toFireStore(): Omit<ExpenseItemModuleData, "uid"> {
    const dataCollected = super.toFireStore() as ExpenseItemModuleData;

    return {
      ...dataCollected,
      amount: this.data.amount.toNumber() as unknown as Big,
      splits: this.data.splits.map((split) => ({
        ...split,
        amount: split.amount.toNumber() as unknown as Big,
      })),
    };
  }

  render() {
    return <View />;
  }

  static is(
    module: BaseModuleDataModel<BaseModuleModelData>,
  ): module is ExpenseItemModule {
    return module instanceof ExpenseItemModule;
  }
}
